import { create } from 'zustand';
import { persist, subscribeWithSelector } from 'zustand/middleware';
import type { WealthicaAccount, WealthicaConnection } from '@/providers/wealthica2/types';
// WealthicaStorage import removed - using direct localStorage access
import { WealthicaConnectionStatus } from '@/providers/wealthica2/constants-v2';
import { createMultiAccountActions } from '@/providers/wealthica2/store/multi-account-actions';
import { createPersistenceActions } from '@/providers/wealthica2/store/persistence-actions';
// No direct identifier logic here; handled inside action modules
// Persistence implementation is provided by createPersistenceActions
import { createSelectors } from './multiWealthica2Store/selectors';
import { storeLogger, EVENTS } from './multiWealthica2Store/logging';
import { createHydrationActions, schedulePostHydrationAutoRefetch, rehydratePersistedState } from './multiWealthica2Store/hydration';
import { createConnectionActions } from './multiWealthica2Store/connection-actions';
import { createBatchActions } from './multiWealthica2Store/batch-actions';
import { createAccountStateActions } from './multiWealthica2Store/account-state-actions';
import { createPositionStateActions } from './multiWealthica2Store/position-state-actions';
import { createRestorationActions } from './multiWealthica2Store/restoration-actions';
import { createStatusStateActions } from './multiWealthica2Store/status-state-actions';

// Use the official status enum for consistency
type ConnectionStatus = keyof typeof WealthicaConnectionStatus;

// Processing states for internal operations (subset of connection status)
const ProcessingState = {
  PROCESSING: 'processing',
  COMPLETE: 'complete', 
  FAILED: 'failed'
} as const;

type ProcessingStateType = typeof ProcessingState[keyof typeof ProcessingState];

export interface ConnectionState {
  // Connection metadata
  connectionData: WealthicaConnection;
  bearerToken: string;
  institutionId: string;
  providerName: string;
  
  // Connection status - use official enum values for full parity
  status: 'idle' | 'connecting' | 'connected' | 'loading_accounts' | 'disconnected' | 'error' | 'pending';
  isConnected: boolean;
  error: string | null;
  
  // Account data
  accounts: WealthicaAccount[];
  isLoadingAccounts: boolean;
  accountsError: string | null;
  lastAccountsFetch: Date | null;
  
  // Position data
  positions: unknown[] | null;
  isLoadingPositions: boolean;
  positionsError: string | null;
  lastPositionsFetch: Date | null;
  
  // Processing state
  isProcessing: boolean;
  processingState: ProcessingStateType;
  
  // Persistence state
  isPersisting: boolean;
  persistenceError: string | null;
}

interface MultiConnectionState {
  // Core multi-connection state
  connections: Map<string, ConnectionState>;
  activeConnectionId: string | null;
  
  // Global preferences
  persistDataPreference: boolean;
  
  // Global loading states
  isInitializing: boolean;
  initializationError: string | null;
  
  // Global batch deduplication
  isFetchingAllConnections: boolean;
  lastBatchFetch: Date | null;
  
  // Restoration coordination
  restorationAttempted: boolean;
  restorationInProgress: boolean;
  restorationSource: string | null;
  hydrationCompleted: boolean;
}

interface MultiConnectionActions {
  // Connection management
  addConnection: (institutionId: string, connectionData: WealthicaConnection, bearerToken: string) => void;
  removeConnection: (institutionId: string) => void;
  getConnection: (institutionId: string) => ConnectionState | undefined;
  getAllConnections: () => ConnectionState[];
  hasConnection: (institutionId: string) => boolean;
  
  // Database synchronization
  initializeFromDatabase: (userId: string) => Promise<void>;
  
  // Account fetching actions
  fetchAccountsForInstitution: (
    institutionId: string,
    isNavigation?: boolean,
    forceRefresh?: boolean,
    persistOptions?: { toastId?: string | number; suppressSuccessToast?: boolean; awaitPersistence?: boolean }
  ) => Promise<void>;
  fetchAccountsForAllConnections: (isNavigation?: boolean, forceRefreshInstitutionId?: string) => Promise<void>;
  refreshAccountsForInstitution: (institutionId: string, options?: { toastId?: string | number }) => Promise<void>;
  getAggregatedAccountData: () => unknown;
  clearAccountsForInstitution: (institutionId: string) => void;
  clearAllAccounts: () => void;
  
  // Connection status management
  setConnectionStatus: (institutionId: string, status: ConnectionState['status']) => void;
  setConnectionError: (institutionId: string, error: string | null) => void;
  clearConnectionError: (institutionId: string) => void;
  
  // Account management per institution
  setAccountsForInstitution: (institutionId: string, accounts: WealthicaAccount[]) => void;
  setAccountsLoadingForInstitution: (institutionId: string, loading: boolean) => void;
  setAccountsErrorForInstitution: (institutionId: string, error: string | null) => void;
  
  // Position management per institution  
  setPositionsForInstitution: (institutionId: string, positions: unknown[]) => void;
  setPositionsLoadingForInstitution: (institutionId: string, loading: boolean) => void;
  setPositionsErrorForInstitution: (institutionId: string, error: string | null) => void;
  clearPositionsForInstitution: (institutionId: string) => void;
  loadPositionsForInstitution: (institutionId: string) => Promise<void>;
  fetchPositionsForInstitution: (institutionId: string) => Promise<void>;
  
  // Processing state per institution
  setProcessingForInstitution: (institutionId: string, processing: boolean) => void;
  setProcessingStateForInstitution: (institutionId: string, state: ProcessingStateType) => void;
  
  // Persistence per institution
  setPersistingForInstitution: (institutionId: string, persisting: boolean) => void;
  setPersistenceErrorForInstitution: (institutionId: string, error: string | null) => void;
  
  // Aggregated data selectors
  getAllAccounts: () => WealthicaAccount[];
  getAllPositions: () => unknown[];
  getTotalAccountValue: () => number;
  getConnectionCount: () => number;
  
  // UI compatibility helpers
  isAnyLoadingAccounts: () => boolean;
  
  // UI Compatibility helpers for migration
  isAnyConnected: () => boolean;
  getOverallStatus: () => 'connected' | 'disconnected' | 'error';
  getAggregatedAccountsError: () => string | null;
  getAggregatedLoadingState: () => boolean;
  getLastAccountsFetch: () => Date | null;
  getLastSyncTimestamp: () => string | null;
  
  // Global actions
  setActiveConnection: (institutionId: string | null) => void;
  setPersistDataPreference: (value: boolean) => void;
  clearAllData: () => void;
  resetState: () => void;
  
  // Multi-store persistence actions
  persistAccountsAndPositions: (
    userId: string, 
    connectionData: WealthicaConnection, 
    accounts: WealthicaAccount[], 
    forceLocalOrOptions?: boolean | { isRefresh?: boolean; forceLocal?: boolean; toastId?: string | number; suppressSuccessToast?: boolean }
  ) => Promise<void>;
  deleteAccountData: (
    userId: string, 
    connectionData: WealthicaConnection, 
    preserveConnectionOrOptions?: boolean | { preserveConnection?: boolean; deleteMode?: 'stop_syncing' | 'full_delete' }
  ) => Promise<void>;
  setPersistenceError: (error: string | null) => void;
  
  // Restoration coordination
  markRestorationAttempted: (source: string) => void;
  completeRestoration: () => void;
  resetRestorationState: () => void;
}

type MultiWealthica2State = MultiConnectionState & MultiConnectionActions;

export const useMultiWealthica2Store = create<MultiWealthica2State>()(
  persist(
    (set, get) => ({
      // === COMPOSED ACTIONS FROM MODULES ===
      ...createMultiAccountActions(set, get),
      ...createBatchActions(set, get),
      ...createPersistenceActions(set, get),
      ...createHydrationActions(set, get),
      ...createConnectionActions(set, get),
      ...createAccountStateActions(set, get),
      ...createPositionStateActions(set, get),
      ...createRestorationActions(set, get),
      ...createStatusStateActions(set, get),

      // Initial state
      connections: new Map(),
      activeConnectionId: null,
      persistDataPreference: true,
      isInitializing: false,
      initializationError: null,
      isFetchingAllConnections: false,
      lastBatchFetch: null,
      restorationAttempted: false,
      restorationInProgress: false,
      restorationSource: null,
      hydrationCompleted: false,

      // Connection actions are provided by createConnectionActions

      // === ACCOUNT & POSITION STATE: moved to account-state-actions.ts and position-state-actions.ts ===

      // Status/persistence setters moved to status-state-actions.ts

      // === AGGREGATED DATA SELECTORS ===
      getAllAccounts: () => createSelectors(get).getAllAccounts(),

      getAllPositions: () => createSelectors(get).getAllPositions(),

      getTotalAccountValue: () => createSelectors(get).getTotalAccountValue(),

      getConnectionCount: () => createSelectors(get).getConnectionCount(),

      // === UI COMPATIBILITY HELPERS ===
      isAnyConnected: () => createSelectors(get).isAnyConnected(),

      isAnyLoadingAccounts: () => createSelectors(get).isAnyLoadingAccounts(),

      getOverallStatus: () => createSelectors(get).getOverallStatus(),

      getAggregatedAccountsError: () => createSelectors(get).getAggregatedAccountsError(),

      getAggregatedLoadingState: () => createSelectors(get).getAggregatedLoadingState(),

      getLastAccountsFetch: () => createSelectors(get).getLastAccountsFetch(),

      getLastSyncTimestamp: () => createSelectors(get).getLastSyncTimestamp(),

      // Restoration actions extracted to restoration-actions.ts
    }),
    {
      name: 'wealthica2-multi-store',
      partialize: (state) => ({
        connections: Array.from(state.connections.entries()),
        activeConnectionId: state.activeConnectionId,
        persistDataPreference: state.persistDataPreference,
        restorationAttempted: state.restorationAttempted,
        restorationSource: state.restorationSource,
        // Persist last batch fetch timestamp to keep short-term cache between navigations
        lastBatchFetch: state.lastBatchFetch
      }),
      merge: (persistedState: unknown, currentState) => {
        const mergedState = rehydratePersistedState(persistedState, currentState) as unknown as MultiWealthica2State;
        // Schedule post-hydration auto-refetch using helper (keeps logic in hydration module)
        if (mergedState.connections && mergedState.connections.size > 0) {
          schedulePostHydrationAutoRefetch(() => useMultiWealthica2Store.getState());
        }
        return mergedState;
      }
    }
  )
);
